import { AfterViewInit, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Chart } from 'angular-highcharts';

@Component({
    selector: 'app-highcharts-generic',
    templateUrl: './highcharts-generic.component.html',
    styleUrls: ['./highcharts-generic.component.scss'],
    standalone: false
})
export class HighchartsGenericComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
    @Input() options: any;
    @Input() series: Array<any>;
    @Input() chartheight = '200px';

    highchart: Chart;

    // Highcharts reads its container width when the chart is constructed, and angular-highcharts
    // never reflows on its own. Inside lazy <ng-template matTabContent> + flex containers that
    // width can be 0/not-yet-final at construction time, so reflow as the layout settles and on resize.
    private resizeObserver?: ResizeObserver;
    private reflowTimers: any[] = [];
    private lastWidth = 0;

    constructor(
        // MAT_DIALOG_DATA / MatDialogRef only exist when this component is opened as a dialog.
        // When used inline (e.g. <app-highcharts-generic> in a tab) they are absent, so they must
        // be optional — otherwise the component throws NG0201 on construction and never renders.
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
        @Optional() public dialogRef: MatDialogRef<HighchartsGenericComponent>,
        private elementRef: ElementRef
    ) {
        if (this.data?.options) {
            this.options = this.data.options;
        }

        if (this.data?.series) {
            this.series = this.data.series;
        }

        if (this.data?.height) {
            this.chartheight = this.data.height;
        }
    }

    ngOnInit() {
        this.initChart();
    }

    ngOnChanges() {
        this.initChart();
        this.scheduleReflows();
    }

    ngAfterViewInit() {
        this.scheduleReflows();

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver((entries) => {
                const width = entries[0]?.contentRect?.width ?? 0;
                if (width > 0 && width !== this.lastWidth) {
                    this.lastWidth = width;
                    this.reflow();
                }
            });
            this.resizeObserver.observe(this.elementRef.nativeElement);
        }
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        this.reflowTimers.forEach((t) => clearTimeout(t));
    }

    private scheduleReflows() {
        [0, 150, 350].forEach((delay) => {
            this.reflowTimers.push(setTimeout(() => this.reflow(), delay));
        });
    }

    private reflow() {
        // ref$ is an AsyncSubject: it replays the chart instance to this subscriber (or fires once
        // it's created) and completes, so there is nothing to unsubscribe.
        this.highchart?.ref$?.subscribe((chart) => {
            try {
                chart?.reflow();
            } catch {
                // chart may be mid-recreate / already destroyed — safe to ignore.
            }
        });
    }

    initChart() {
        if (!this.options || this.options === 'undefined') {
            return;
        }

        this.highchart = new Chart(this.options);
        if (this.series) {
            for (const chunk of this.series) {
                this.highchart.addSeries(chunk, true, true);
            }
        }
    }
}
